<script lang="ts" setup>
// -------------------------------
// 🔹 Imports Vue et composables
// -------------------------------
import { computed, toRaw, watch } from 'vue';
import { useQuery } from '@tanstack/vue-query';

// -------------------------------
// 🔹 Imports composants
// -------------------------------
import SidebarLayout from '@/components/layout/SidebarLayout.vue';
import BaseCheckBox from '@/components/common/BaseCheckBox.vue';
import BaseButtonPrimary from '@/components/common/BaseButtonPrimary.vue';

// -------------------------------
// 🔹 Imports stores Pinia
// -------------------------------
import { useDialogStore } from '@/store/dialog.store';
import { useCategoryStore } from '@/store/category.store';

// -------------------------------
// 🔹 Imports utilitaires
// -------------------------------
import { getDateFormat, getDateHourMinute } from '@/utils/formatDate';

// -------------------------------
// 🔹 Imports assets / svg
// -------------------------------
import { arrwo_inline, dots } from '@/assets/svg/arraw_pagination';

// -------------------------------
// 🔹 Imports refs / données locales
// -------------------------------
import {
  slice_paginate_order,
  data_array_from_filter_order,
} from '@/refs/dashboard.refs';
import { category_th } from '@/data/category.mock';
import { idToDelete } from '@/refs/commerce.refs';
import {
  form_CategorieBody,
  form_CatDesignation,
  idCategory,
  isCategoryActivated,
  isDeletedFromCategory,
  pageCategory,
} from '@/refs/categories.refs';
import { globalHeaderTitleLayout } from '@/refs/common.refs';
import BaseNotDataFound from '@/components/common/BaseNotDataFound.vue';
import { searchIcon } from '@/assets/svg/common.svg';
import { restaurantId } from '@/refs/common.refs';
import { searchQueryCategory } from '../../refs/categories.refs';
import { useCommerceStore } from '@/store/commerce.store';
import {
  categoryIds_product_promotion,
  isPromoCategoryStep,
} from '@/refs/promotion.refs';
// -------------------------------
// 🔹 Stores instanciés
// -------------------------------

const dialogueStore = useDialogStore();
const categoryStore = useCategoryStore();
const commerceStore = useCommerceStore();
// -------------------------------
// 🔹 Récupération des catégories avec Vue Query
// -------------------------------
const {
  data: dataGetCategories,
  isLoading: isLoadingGetCategories,
  isError: isErrorGetCategories,
  refetch: refectGetCategories,
} = useQuery({
  queryKey: ['GetCategories'],
  queryFn: () => categoryStore.getCategory(),
});
const {
  data: dataGetCommerces,
  isLoading: isLoadingGetCommerces,
  isError: isErrorGetCommerces,
  refetch: refectGetCommerces,
} = useQuery({
  queryKey: ['GetCommerces'],
  queryFn: () => commerceStore.getCommerces(),
});

// -------------------------------
// 🔹 Calcul du nombre total de pages
// -------------------------------
const totalPages = computed(() =>
  Math.ceil(
    Number(dataGetCategories?.value?.total) /
      Number(dataGetCategories?.value?.pageSize),
  ),
);

// -------------------------------
// 🔹 Watcher pour recharger les catégories lors du changement de page ou d'ouverture du dialogue
// -------------------------------
// watch(
//   [pageCategory.value, dialogueStore.visible],
//   ([newPage, newDialogue], [oldPage, oldDialogue]) => {
//     refectGetCategories();
//   },
// );

watch(
  () => dialogueStore.visible,
  (newVal, oldVal) => {
    refectGetCategories();
  },
);
// -------------------------------
// 🔹 Pagination
// -------------------------------
const handlePaginate = (pageNumber: number) => {
  const totalPagess = Array.from(
    { length: Number(totalPages.value) },
    (_, i) => i + 1,
  );
  const startIndex = Math.max(pageNumber - 1, 0);
  data_array_from_filter_order.value = totalPagess.slice(
    startIndex,
    startIndex + 3,
  );
  pageCategory.value = pageNumber;
};

const handleNextPage = () => {
  const totalPagess = Array.from(
    { length: Number(totalPages.value) },
    (_, i) => i + 1,
  );
  const startIndex = pageCategory.value;
  data_array_from_filter_order.value = totalPagess.slice(
    startIndex,
    startIndex + 3,
  );
  pageCategory.value =
    totalPagess[startIndex] || totalPagess[totalPagess.length - 1];
  slice_paginate_order.value = startIndex;
};

const handlePreviousPage = () => {
  if (pageCategory.value === 1) return;
  const totalPagess = Array.from(
    { length: Number(totalPages.value) },
    (_, i) => i + 1,
  );
  let startIndex = Number(toRaw(pageCategory.value)) - 3;
  if (startIndex < 0) startIndex = 0;
  data_array_from_filter_order.value = totalPagess.slice(
    startIndex,
    startIndex + 3,
  );
  pageCategory.value = totalPagess[startIndex];
  slice_paginate_order.value = startIndex;
};

// -------------------------------
// 🔹 Dialogues
// -------------------------------
const openDialogue = (data: any) => {
  if (toRaw(data) && toRaw(data)?.id) {
    idCategory.value = toRaw(data)?.id;
    globalHeaderTitleLayout.value = 'Modifier cette catégorie';
    form_CategorieBody.description = toRaw(data)?.description;
    form_CategorieBody.designation = toRaw(data)?.designation;
    form_CatDesignation.fr = toRaw(data)?.designation ?? '';
    form_CatDesignation.nl = '';
    form_CatDesignation.en = '';
    isCategoryActivated.value = true;
  } else {
    globalHeaderTitleLayout.value = 'Ajouter une nouvelle catégorie';
  }

  dialogueStore.openDialog({
    isDeleted: false,
    isAddedFromCommerce: false,
    isAddedFromCategoy: true,
  });
};

const openDialogueForDelete = (data: any) => {
  idToDelete.value = String(toRaw(data)?.id);
  isDeletedFromCategory.value = true;
  console.log(toRaw(data)?.id);
  dialogueStore.openDialog({
    isDeleted: true,
  });
};
</script>

<template>
  <section class="flex w-full flex-row items-center justify-between gap-4">
    <div v-show="!isPromoCategoryStep">
      <h1
        class="text-25px text-grand-text font-tt-semiBold-Normal leading-[35px]"
      >
        Catégpries ({{ dataGetCategories?.total }})
      </h1>
      <p
        class="text-16px text-secondary-text font-tt-normal-Normal leading-normal"
      >
        Catégorie / Liste des catégories
      </p>
    </div>

    <div class="flex w-full flex-1 flex-row gap-3">
      <div class="flex flex-1 flex-col gap-2">
        <div
          class="relative mx-auto flex w-full max-w-md flex-1 flex-col gap-2"
        >
          <div
            class="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 transform"
            v-html="searchIcon"
          ></div>

          <input
            type="text"
            v-model="searchQueryCategory"
            placeholder="Rechercher par nom de la catégorie"
            class="border-border-input focus:ring-primary-900 focus:border-primary-900 hover:border-border-input w-full rounded-xl border bg-white py-2 pr-3 pl-10 text-gray-700 placeholder-gray-400 shadow-sm transition-all focus:ring-2 focus:outline-none"
          />
        </div>
      </div>
      <div class="flex w-full flex-1 flex-col gap-2">
        <select
          id="commerce"
          name="commerce_associé"
          v-model="restaurantId"
          required
          class="border-border-input focus:ring-primary-900 focus:border-primary-900 w-full rounded-xl border bg-white px-3 py-2 text-sm text-gray-700 placeholder-gray-400 shadow-sm transition-all outline-none hover:border-gray-400 focus:ring-2"
        >
          <option value="" disabled>Veuillez sélectionner un commerce</option>
          <option
            v-for="value in dataGetCommerces?.data"
            :value="value?.id"
            class="text-14px text-secondary-text"
          >
            {{ value.designation }}
          </option>
        </select>
      </div>
    </div>
    <div @click="openDialogue" v-show="!isPromoCategoryStep">
      <BaseButtonPrimary
        type="submit"
        label="Créer une nouvelle catégorie"
        style_of_btn="primary_color_of_btn "
        class="text-white"
      />
    </div>
  </section>

  <section class="space-y-[16px] rounded-2xl bg-white p-4">
    <section>
      <Table
        class="text-grand-text text-14px font-tt-normal-Twice w-full table-auto"
      >
        <thead class="text-secondary-text text-12px">
          <tr>
            <th scope="col" class="py-block-17px">Index</th>
            <th
              scope="col"
              v-for="(_category_th, index) in category_th"
              :key="index"
              class="font-tt-normal-Twice! py-block-17px text-secondary-text leading-normal font-normal!"
            >
              {{
                isPromoCategoryStep && _category_th?.title === 'Actions'
                  ? ''
                  : _category_th?.title
              }}
            </th>
          </tr>
        </thead>

        <Tbody>
          <tr
            v-if="dataGetCategories?.data.length !== 0"
            v-for="(
              _dataGetCategories, index
            ) in dataGetCategories?.data.filter((i) =>
              i?.designation.includes(searchQueryCategory),
            )"
            :key="index"
            class="border-border-input borde border-b border-solid text-center font-normal"
          >
            <td class="py-block-17px">
              {{ index + 1 }}
            </td>

            <td class="py-block-17px">
              {{ _dataGetCategories.designation }}
            </td>
            <td class="py-block-17px w-[120px]">
              {{
                _dataGetCategories.description === ''
                  ? 'Cette catégorie est vide pour l’instant. Ajoutez une description pour la compléter'
                  : _dataGetCategories.description
              }}
            </td>
            <td class="py-block-17px">
              <p>{{ _dataGetCategories.restaurant.designation }}</p>
            </td>

            <td class="py-block-17px">
              {{ getDateFormat(_dataGetCategories.createdAt) }} à
              {{ getDateHourMinute(_dataGetCategories.createdAt) }}
            </td>
            <td class="py-block-17px">
              {{ getDateFormat(_dataGetCategories.updatedAt) }} à
              {{ getDateHourMinute(_dataGetCategories.updatedAt) }}
            </td>

            <td v-show="!isPromoCategoryStep" class="py-block-17px space-y-2">
              <p
                @click="openDialogueForDelete(_dataGetCategories)"
                class="text-decroissance cursor-pointer hover:underline"
              >
                Suprimer
              </p>
              <p
                @click="openDialogue(_dataGetCategories)"
                class="text-selection cursor-pointer hover:underline"
              >
                Modifier
              </p>
            </td>
          </tr>

          <tr v-else>
            <BaseNotDataFound />
          </tr>
        </Tbody>
      </Table>
    </section>

    <section class="flex flex-row items-center justify-between">
      <section class="flex flex-row items-center gap-[8px]">
        <p
          class="text-14px text-secondary-text font-tt-normal-Normal leading-5"
        >
          Résultats par page
        </p>

        <p
          class="text-14px text-secondary-text font-tt-normal-Normal text-center leading-5 tabular-nums"
        >
          {{ dataGetCategories?.pageSize }}
        </p>
      </section>

      <section
        class="flex flex-row items-center gap-2"
        v-show="Number(totalPages - 1) !== 0"
      >
        <div
          v-html="arrwo_inline"
          @click="handlePreviousPage"
          :class="Number(pageCategory) === 1 ? 'opacity-[0.4]' : 'opacity-100'"
          class="flex rotate-180 transform cursor-pointer items-center justify-center"
        />

        <div
          class="flex flex-row items-center gap-[16px]"
          v-show="Number(totalPages - 1) !== 0"
        >
          <p
            @click="handlePaginate(number)"
            class="text-12px font-tt-normal-Normal cursor-pointer"
            v-for="(number, index) in data_array_from_filter_order"
            :key="index"
            :class="
              Number(pageCategory) === number
                ? 'text-selection bg-fond-beige'
                : 'text-grand-text bg-white'
            "
          >
            {{ number }}
          </p>
        </div>

        <div
          v-show="Number(totalPages - 1) !== 0"
          @click="handleNextPage"
          v-html="dots"
          class="flex cursor-pointer items-center justify-center p-[13px]"
        />

        <div
          class="text-12px text-grand-text font-tt-normal-Normal cursor-pointer"
          @click="handlePaginate(totalPages - 1)"
        >
          {{ totalPages - 1 === 0 ? '1' : totalPages }}
        </div>

        <div
          v-show="Number(totalPages - 1) !== 0"
          v-html="arrwo_inline"
          @click="handleNextPage"
          class="flex cursor-pointer items-center justify-center"
        />
      </section>
    </section>
  </section>
</template>
